import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { resolveImageUrl } from '../lib/imageUrl';
import { MapPin, Calendar, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectsPage() {
  const { t, getLocalizedContent } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = filter === 'all'
    ? projects
    : projects.filter(p => p.category === filter);

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div data-testid="projects-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <span className="inline-block px-4 py-1 bg-[#0B6AB3]/20 text-[#0B6AB3] text-sm font-medium rounded-full mb-4">
              Portfolio
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-white font-['Montserrat']">
              {t('projects_title')}
            </h1>
            <p className="text-gray-300 mt-4 max-w-2xl mx-auto text-lg">
              {t('projects_subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Tabs */}
          <div className="flex justify-center mb-12">
            <Tabs value={filter} onValueChange={setFilter} className="w-auto">
              <TabsList className="bg-[#F8F9FA]" data-testid="project-filters">
                <TabsTrigger value="all" data-testid="filter-all">{t('projects_all')}</TabsTrigger>
                <TabsTrigger value="completed" data-testid="filter-completed">{t('projects_completed')}</TabsTrigger>
                <TabsTrigger value="ongoing" data-testid="filter-ongoing">{t('projects_ongoing')}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Projects */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B6AB3]"></div>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              Aucun projet trouvé
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card
                    className="overflow-hidden border-0 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer group"
                    onClick={() => setSelectedProject(project)}
                    data-testid={`project-card-${index}`}
                  >
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={resolveImageUrl(project.images?.[0]) || 'https://via.placeholder.com/400x300'}
                        alt={getLocalizedContent(project.title)}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      <span className={`absolute top-4 right-4 px-3 py-1 text-xs font-medium rounded-full ${
                        project.category === 'completed' ? 'bg-green-500' : 'bg-[#0B6AB3]'
                      } text-white`}>
                        {project.category === 'completed' ? t('projects_completed') : t('projects_ongoing')}
                      </span>
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="font-semibold text-xl text-white mb-2">
                          {getLocalizedContent(project.title)}
                        </h3>
                        <div className="flex items-center gap-4 text-white/80 text-sm">
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {project.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {project.year}
                          </span>
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-5">
                      <p className="text-gray-600 text-sm line-clamp-2">
                        {getLocalizedContent(project.description)}
                      </p>
                      <div className="mt-4 flex items-center justify-between">
                        <span className="text-sm text-gray-500">{project.client}</span>
                        <span className="text-[#0B6AB3] font-medium text-sm flex items-center">
                          {t('projects_view')}
                          <ArrowRight className="ml-1 h-4 w-4" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Project Detail Modal */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="project-modal">
          {selectedProject && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">
                  {getLocalizedContent(selectedProject.title)}
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4">
                <img
                  src={resolveImageUrl(selectedProject.images?.[0]) || 'https://via.placeholder.com/800x400'}
                  alt={getLocalizedContent(selectedProject.title)}
                  className="w-full h-64 md:h-80 object-cover rounded-lg"
                />
                <div className="mt-6 grid md:grid-cols-3 gap-4">
                  <div className="bg-[#F8F9FA] p-4 rounded-lg">
                    <span className="text-sm text-gray-500">Client</span>
                    <p className="font-semibold text-[#0F172A]">{selectedProject.client || 'N/A'}</p>
                  </div>
                  <div className="bg-[#F8F9FA] p-4 rounded-lg">
                    <span className="text-sm text-gray-500">Lieu</span>
                    <p className="font-semibold text-[#0F172A]">{selectedProject.location}</p>
                  </div>
                  <div className="bg-[#F8F9FA] p-4 rounded-lg">
                    <span className="text-sm text-gray-500">Année</span>
                    <p className="font-semibold text-[#0F172A]">{selectedProject.year}</p>
                  </div>
                </div>
                <div className="mt-6">
                  <h4 className="font-semibold text-[#0F172A] mb-2">Description</h4>
                  <p className="text-gray-600 leading-relaxed">
                    {getLocalizedContent(selectedProject.description)}
                  </p>
                </div>
                <div className="mt-6">
                  <span className={`inline-block px-4 py-2 text-sm font-medium rounded-full ${
                    selectedProject.category === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-[#0B6AB3]'
                  }`}>
                    {selectedProject.category === 'completed' ? 'Projet terminé' : 'Projet en cours'}
                  </span>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
